import { useState, useEffect, useCallback } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { TagBadge } from '@/components/ui/badge';
import { useApp } from '@/context/AppContext';
import { useData } from '@/context/DataContext';
import { useDebounce } from '@/hooks/useDebounce';
import { getAllTags } from '@/utils/search';
import { cn } from '@/utils/cn';

// ============================================
// SEARCH BAR
// ============================================

export function SearchBar() {
  const { searchQuery, setSearchQuery, activeTag, setActiveTag } = useApp();

  // Local state for immediate input feedback
  const [localQuery, setLocalQuery] = useState(searchQuery);

  // Debounce the search query
  const debouncedQuery = useDebounce(localQuery, 300);

  // Update global state when debounced query changes
  useEffect(() => {
    setSearchQuery(debouncedQuery);
  }, [debouncedQuery, setSearchQuery]);

  // Sync local state with global state (for external changes)
  useEffect(() => {
    setLocalQuery(searchQuery);
  }, [searchQuery]);

  // Clear search and tag
  const handleClear = useCallback(() => {
    setLocalQuery('');
    setSearchQuery('');
    setActiveTag(null);
  }, [setSearchQuery, setActiveTag]);

  // Clear just the active tag
  const handleClearTag = useCallback(() => {
    setActiveTag(null);
  }, [setActiveTag]);

  const hasActiveFilters = localQuery.trim() || activeTag;

  return (
    <div className="bg-[var(--bg-secondary)] border-b border-[var(--border-primary)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search input */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-tertiary)]" />
            <Input
              type="search"
              value={localQuery}
              onChange={(e) => setLocalQuery(e.target.value)}
              placeholder="Search items..."
              className="pl-10 pr-10"
              aria-label="Search items"
            />
            {localQuery && (
              <button
                type="button"
                onClick={() => {
                  setLocalQuery('');
                  setSearchQuery('');
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Clear all filters button */}
          {hasActiveFilters && (
            <Button variant="outline" size="sm" onClick={handleClear} className="shrink-0">
              <X className="h-4 w-4 mr-1" />
              Clear filters
            </Button>
          )}
        </div>

        {/* Active tag filter indicator */}
        {activeTag && (
          <div className="mt-3 flex items-center gap-2">
            <span className="text-sm text-[var(--text-tertiary)]">Filtering by tag:</span>
            <TagBadge tag={activeTag} removable onRemove={handleClearTag} />
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// TAG FILTER PILLS (optional standalone component)
// ============================================

interface TagFilterProps {
  className?: string;
}

export function TagFilter({ className }: TagFilterProps) {
  const { activeTag, setActiveTag } = useApp();
  const { allItems } = useData();

  // Get all unique tags sorted by frequency
  const allTags = getAllTags(allItems);

  if (allTags.length === 0) return null;

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {allTags.slice(0, 10).map((tag) => (
        <TagBadge
          key={tag}
          tag={tag}
          onClick={() => setActiveTag(activeTag === tag ? null : tag)}
          className={cn(
            'cursor-pointer transition-all',
            activeTag === tag && 'ring-2 ring-brand-500 ring-offset-1'
          )}
        />
      ))}
      {allTags.length > 10 && (
        <span className="text-sm text-[var(--text-tertiary)] self-center">
          +{allTags.length - 10} more
        </span>
      )}
    </div>
  );
}

// ============================================
// COMPACT SEARCH (for mobile)
// ============================================

export function CompactSearch() {
  const { searchQuery, setSearchQuery } = useApp();
  const [isExpanded, setIsExpanded] = useState(false);
  const [localQuery, setLocalQuery] = useState(searchQuery);
  const debouncedQuery = useDebounce(localQuery, 300);

  useEffect(() => {
    setSearchQuery(debouncedQuery);
  }, [debouncedQuery, setSearchQuery]);

  if (!isExpanded) {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsExpanded(true)}
        aria-label="Open search"
      >
        <Search className="h-5 w-5" />
      </Button>
    );
  }

  return (
    <div className="absolute inset-x-0 top-0 bg-[var(--bg-secondary)] p-4 shadow-lg z-50">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-tertiary)]" />
          <Input
            type="search"
            value={localQuery}
            onChange={(e) => setLocalQuery(e.target.value)}
            placeholder="Search..."
            className="pl-10"
            autoFocus
          />
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setIsExpanded(false);
            setLocalQuery('');
            setSearchQuery('');
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
